// DashboardDetailsService.ts
import api from "./api";
import { Filtros, buildQueryParams } from "./filterService";

export interface MesValor {
  categoria: string;   // Ex: "Jan"
  realizado: number;   // VGV realizado
  projecao: number;    // VGV projetado
}

export interface EstoquePorTipo {
  Lote: number;
  Apartamento: number;
  Comercial: number;
  Casa: number;
  Compacto: number;
}

export interface EstoquePorFase {
  Planta: number;
  Obra: number;
  Pronto: number;
}

export interface IntervaloQuantidade {
  intervalo: string;
  quantidade: number;
}


export interface PrecoMedioMes {
  categoria: string; 
  valor: number;     
}

export interface EvolucaoVendasData {
  vendas_por_mes: Record<string, number>; // Ex: { January: 3, February: 4, ... }
  media: {
    media_vendas_ultimos_12_meses: number;
  };
}

export interface EvolucaoVendasItem {
  mes: string; // Ex: "Jan"
  Atual: number;
  Media: number;
}

export interface ItemCategoria {
  item: string;
  quantidade: number;
}

export interface ItensPorCategoria {
  Lazer: ItemCategoria[];
  Infraestrutura: ItemCategoria[];
  Segurança: ItemCategoria[];
}

export interface IntervalDataItem {
  intervalo: string;
  quantidade: number;
}

export interface TipologiaData {
  ofertadas: Record<string, number>;
  vendidas: Record<string, number>;
  estoque: Record<string, number>;
}

export interface UnidadesDashboard {
  tipologia: TipologiaData;
  area: TipologiaData;
  cidades: TipologiaData;
}

export interface IndicadoresDashboardDetalhado {
  cidades: number;
  construtoras: number;
  empreendimentos: number;
  unidades: number;
  vso: number; // já vem em percentual (0-100)
}


export const getVGVDashboard = async (filtros: Filtros): Promise<MesValor[]> => {
  try {
    const query = buildQueryParams(filtros);
    const response = await api.get(`/vgv-dashboard-Detalhado/?${query}`);

    const mesesTraducao: Record<string, string> = {
      January: "Jan",
      February: "Fev",
      March: "Mar",
      April: "Abr",
      May: "Mai",
      June: "Jun",
      July: "Jul",
      August: "Ago",
      September: "Set",
      October: "Out",
      November: "Nov",
      December: "Dez",
    };

    const dados: MesValor[] = Object.entries(response.data).map(
      ([mes, valores]: [string, any]) => ({
        categoria: mesesTraducao[mes] ?? mes,
        realizado: Number(valores.realizado ?? 0),
        projecao: Number(valores.projecao ?? 0),
      })
    );

    return dados;
  } catch (error) {
    console.error("Erro ao buscar VGV detalhado:", error);
    throw error;
  }
};

// Função para buscar estoque por tipo
export const getEstoquePorTipo = async (filtros?: any): Promise<EstoquePorTipo> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/estoque-empreendimento-Por-tipo/${query}`);
    return response.data; // espera { Lote: 1, Apartamento: 5, ... }
  } catch (error) {
    console.error('Erro ao buscar estoque por tipo', error);
    return { Lote: 0, Apartamento: 0, Comercial: 0, Casa: 0, Compacto: 0 };
  }
};


export const getEstoquePorFase = async (filtros?: any): Promise<EstoquePorFase> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/estoque-empreendimento-por-fase/${query}`);
    return response.data; // { Planta: 20, Obra: 1, Pronto: 0 }
  } catch (error) {
    console.error("Erro ao buscar estoque por fase:", error);
    return { Planta: 0, Obra: 0, Pronto: 0 };
  }
};

// Busca percentual vendido
export const getPercentualVendido = async (filtros?: any): Promise<IntervaloQuantidade[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/percentual-vendido/${query}`);
    // Transforma o objeto em array de {intervalo, quantidade}
    return Object.entries(response.data).map(([intervalo, quantidade]) => ({
      intervalo,
      quantidade: Number(quantidade),
    }));
  } catch (error) {
    console.error("Erro ao buscar percentual vendido:", error);
    return [];
  }
};

// Busca intervalos de área
export const getIntervalosArea = async (filtros?: any): Promise<IntervaloQuantidade[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/area-empreendimento/${query}`);
    return Object.entries(response.data).map(([intervalo, quantidade]) => ({
      intervalo,
      quantidade: Number(quantidade),
    }));
  } catch (error) {
    console.error("Erro ao buscar intervalos de área:", error);
    return [];
  }
};

// Busca preço médio do m² por intervalo de vendas (% vendido)
export const getPrecoMedioM2PorIntervaloVendas = async (filtros?: any): Promise<IntervaloQuantidade[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/preco-medio-M2-por-intervalo-vendas/${query}`);
    return Object.entries(response.data).map(([intervalo, quantidade]) => ({
      intervalo,
      quantidade: Number(quantidade),
    }));
  } catch (error) {
    console.error("Erro ao buscar preço médio por intervalo de vendas", error);
    return [];
  }
};

// Busca preço médio do m² por intervalo de área
export const getPrecoMedioM2PorIntervaloArea = async (filtros?: any): Promise<IntervaloQuantidade[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/preco-medio-por-M2-por-intervalo-area/${query}`);
    return Object.entries(response.data).map(([intervalo, quantidade]) => ({
      intervalo,
      quantidade: Number(quantidade),
    }));
  } catch (error) {
    console.error("Erro ao buscar preço médio por intervalo de área", error);
    return [];
  }
};

// Busca preço médio por mês
export const getPrecoMedioPorMes = async (filtros?: any): Promise<PrecoMedioMes[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/preco-medio-por-mes/${query}`);

    const mesesTraducao: Record<string, string> = {
      January: "Jan",
      February: "Fev",
      March: "Mar",
      April: "Abr",
      May: "Mai",
      June: "Jun",
      July: "Jul",
      August: "Ago",
      September: "Set",
      October: "Out",
      November: "Nov",
      December: "Dez",
    };

    return Object.entries(response.data).map(([mes, valor]) => ({
      categoria: mesesTraducao[mes] ?? mes,
      valor: Number(valor ?? 0),
    }));
  } catch (error) {
    console.error("Erro ao buscar preço médio por mês:", error);
    return [];
  }
};

// Busca preço médio do m² por mês
export const getPrecoMedioM2PorMes = async (filtros?: any): Promise<PrecoMedioMes[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get(`/preco-medio-M2-por-mes/${query}`);

    const mesesTraducao: Record<string, string> = {
      January: "Jan",
      February: "Fev",
      March: "Mar",
      April: "Abr",
      May: "Mai",
      June: "Jun",
      July: "Jul",
      August: "Ago",
      September: "Set",
      October: "Out",
      November: "Nov",
      December: "Dez",
    };

    return Object.entries(response.data).map(([mes, valor]) => ({
      categoria: mesesTraducao[mes] ?? mes,
      valor: Number(valor ?? 0),
    }));
  } catch (error) {
    console.error("Erro ao buscar preço médio do m² por mês:", error);
    return [];
  }
};

export const getEvolucaoVendas = async (filtros?: any): Promise<EvolucaoVendasItem[]> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get<EvolucaoVendasData>(`/evolucao-vendas/${query}`);

    const mesesTraducao: Record<string, string> = {
      January: "Jan",
      February: "Fev",
      March: "Mar",
      April: "Abr",
      May: "Mai",
      June: "Jun",
      July: "Jul",
      August: "Ago",
      September: "Set",
      October: "Out",
      November: "Nov",
      December: "Dez",
    };

    const vendasPorMes = response.data.vendas_por_mes;
    const media12 = response.data.media.media_vendas_ultimos_12_meses;

    return Object.entries(vendasPorMes).map(([mes, valor]) => ({
      mes: mesesTraducao[mes] ?? mes,
      Atual: Number(valor),
      Media: Number(media12),
    }));
  } catch (error) {
    console.error("Erro ao buscar evolução de vendas:", error);
    return [];
  }
};

export const getItensPorCategoria = async (filtros?: any): Promise<ItensPorCategoria> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get<ItensPorCategoria>(`/itens-por-categoria/${query}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar itens por categoria:", error);
    return { Lazer: [], Infraestrutura: [], Segurança: [] };
  }
};

export const getUnidadesDashboard = async (filtros?: any): Promise<UnidadesDashboard> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get<UnidadesDashboard>(`/unidades-dashboard/${query}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar unidades dashboard:", error);
    return {
      tipologia: { estoque: {}, vendidas: {}, ofertadas: {} },
      area: { estoque: {}, vendidas: {}, ofertadas: {} },
      cidades: { estoque: {}, vendidas: {}, ofertadas: {} },
    };
  }
};

export const getIndicadoresDashboardDetalhado = async (filtros?: any): Promise<IndicadoresDashboardDetalhado> => {
  try {
    const query = filtros ? `?${new URLSearchParams(filtros).toString()}` : '';
    const response = await api.get<IndicadoresDashboardDetalhado>(`/indicadores-dashboard-detalhado/${query}`);
    return response.data;
  } catch (error) {
    console.error("Erro ao buscar indicadores detalhados:", error);
    return { cidades: 0, construtoras: 0, empreendimentos: 0, unidades: 0, vso: 0 };
  }
};
